import React from 'react'
import PropTypes from 'prop-types'
import { agnostifyUrl } from '../../../../helpers/utils'
import Image from '../../../../helpers/Image/Image'
import {
  StyledWrapper,
  StyledImage,
  StyledTextContainer,
  StyledHeading,
  StyledSubHeading,
  StyledSponsorCopy,
  StyledChevron,
  StyledLinksContainer,
  StyledLink,
  StyledSignpost,
  StyledSignpostsContainer
} from './M0013.styles'
import { createGolContentDataAttributes, joinContentLinkTitles } from '../../../../helpers/tracking'

const M0013 = ({
  backgroundImageUrl,
  backgroundImageAltText,
  heading,
  isSecondaryHeading,
  backgroundColor,
  textTheme,
  subheading,
  sponsorCopy,
  sponsorLogo,
  sponsorLogoAltText,
  linkTarget,
  links,
  onLinkClick,
  imageFormat
}) => {
  const getGOLDataAttributes = (linkTitle, linkUrl, action) =>
    createGolContentDataAttributes(
      'Isolated',
      'M0013 - NA',
      action === 'Load' ? joinContentLinkTitles(links, [], 'M0013') : linkTitle,
      linkUrl,
      heading,
      action,
      true
    )

  const addGolTracking = (evt, linkUrl, linkTitle) => {
    const dataAttributes = getGOLDataAttributes(linkTitle, linkUrl)
    Object.keys(dataAttributes).forEach((attribute) =>
      evt?.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const signpostLink = (signpost, index) => {
    const onLinkClickFn = (e) => (typeof onLinkClick === 'function' ? onLinkClick(e, signpost.linkUrl) : false)

    return (
      <StyledLink
        data-test={`m0013-link-${index}-title`}
        href={agnostifyUrl(signpost.linkUrl)}
        title={signpost.linkTitle}
        onClick={(e) => {
          onLinkClickFn(e)
          addGolTracking(e, signpost.linkUrl, signpost.linkTitle)
        }}
        backgroundColor={backgroundColor}
        textTheme={textTheme}
        target={linkTarget}
      >
        {signpost.linkName}
        <StyledChevron dir='right' key='icon' />
      </StyledLink>
    )
  }

  if (links.length < 2 || links.length > 8) return null
  const signpostsEvenOdd = links.length % 2 === 0 ? 'even' : 'odd'
  return (
    <StyledWrapper
      data-test='m0013'
      row='xs'
      gutterValue={0}
      {...getGOLDataAttributes('', links?.[0]?.linkUrl, 'Load')}
    >
      {backgroundImageUrl && (
        <StyledImage data-test='m0013-image' alt={backgroundImageAltText} src={backgroundImageUrl} responsive />
      )}
      <StyledTextContainer data-test='m0013_text-container' xs={12} md={6}>
        <StyledHeading as={isSecondaryHeading ? 'h2' : 'h1'} textTheme={textTheme} data-test='m0013_heading'>
          {heading}
        </StyledHeading>
        {subheading && (
          <StyledSubHeading textTheme={textTheme} data-test='m0013_subheading'>
            {subheading}
          </StyledSubHeading>
        )}
        {sponsorCopy && sponsorLogo && (
          <>
            <StyledSponsorCopy textTheme={textTheme} data-test='m0013_sponsor-copy'>
              {sponsorCopy}
            </StyledSponsorCopy>
            <Image
              imageFormat={imageFormat}
              data-test='m0013_sponsor-logo'
              alt={sponsorLogoAltText}
              src={sponsorLogo}
              height={22}
            />
          </>
        )}
      </StyledTextContainer>
      <StyledLinksContainer data-test='m0013-link-container' xs={12} md={6}>
        <StyledSignpostsContainer
          data-test='m0013-link-row-container'
          forwardedAs='ul'
          row='xs'
          signpostsEvenOdd={signpostsEvenOdd}
        >
          {links.map((signpost, index) => (
            <StyledSignpost
              forwardedAs='li'
              data-test={`m0013-link-${index}`}
              xs={6}
              gutterValue={0}
              key={`m0013-signpost-${index}`}
            >
              {signpostLink(signpost, index)}
            </StyledSignpost>
          ))}
        </StyledSignpostsContainer>
      </StyledLinksContainer>
    </StyledWrapper>
  )
}

M0013.propTypes = {
  /** Optional background colour, defaults to white */
  backgroundColor: PropTypes.string,
  /** Optional background image url */
  backgroundImageUrl: PropTypes.string,
  /** Alt text for the background image; should be empty as long as it's decorative */
  backgroundImageAltText: PropTypes.string,
  /** Text for the heading  */
  heading: PropTypes.string,
  /** Optional image format, eg. avif */
  imageFormat: PropTypes.string,
  /** Optional boolean to render an h2 tag instead of h1 for heading */
  isSecondaryHeading: PropTypes.bool,
  /** Array of links to be displayed */
  links: PropTypes.array,
  /** Option of opening in a new tab, defaults to an empty string  */
  linkTarget: PropTypes.oneOf(['', '_blank', '_self']),
  /** Optional function to handle CTA click */
  onLinkClick: PropTypes.func,
  /** Optional text for the sponsor message */
  sponsorCopy: PropTypes.string,
  /** Optional sponsor logo */
  sponsorLogo: PropTypes.string,
  /** Optional alt text for sponsor logo */
  sponsorLogoAltText: PropTypes.string,
  /** Text for sunbheading */
  subheading: PropTypes.string,
  /** Text theme, defaults to light */
  textTheme: PropTypes.string
}

M0013.defaultProps = {
  backgroundImageAltText: '',
  linkTarget: '',
  textTheme: 'light',
  sponsorLogoAltText: '',
  isSecondaryHeading: false
}

export default M0013
